import JSZip from 'jszip';
import type { BannerConfig, PageConfig } from './types.ts';
import {
    dataUrlToBlob,
    escapeHtml,
    safeColor,
    shadowCss,
} from './utils.ts';

function bannerStyle(banner: BannerConfig): string {
    const justify =
        banner.titleAlign === 'left' ? 'flex-start' : banner.titleAlign === 'right' ? 'flex-end' : 'center';
    return [
        `width:100%`,
        `align-self:stretch`,
        `margin:${banner.marginTop}px 0 ${banner.marginBottom}px`,
        `padding:${banner.paddingY}px ${banner.paddingX}px`,
        `min-height:${banner.minHeight}px`,
        `border-radius:${banner.borderRadius}px`,
        `background:${safeColor(banner.backgroundColor, '#162947')}`,
        `color:${safeColor(banner.textColor, '#ffffff')}`,
        `border:${banner.borderWidth}px solid ${safeColor(banner.borderColor, '#ffffff')}`,
        `box-shadow:${shadowCss(banner.shadow)}`,
        `display:flex`,
        `align-items:center`,
        `justify-content:${justify}`,
        `text-decoration:none`,
        `cursor:pointer`,
        `box-sizing:border-box`,
    ].join(';');
}

function titleStyle(banner: BannerConfig): string {
    return [
        `font-family:Anton,sans-serif`,
        `font-size:${banner.fontSize}px`,
        `letter-spacing:${banner.letterSpacing}px`,
        `text-align:${banner.titleAlign}`,
        `text-transform:${banner.uppercase ? 'uppercase' : 'none'}`,
        `color:${safeColor(banner.textColor, '#ffffff')}`,
        `line-height:1.15`,
        `width:100%`,
        `margin:0`,
    ].join(';');
}

function buildCss(page: PageConfig): string {
    return `* { box-sizing: border-box; }
html, body { margin: 0; min-height: 100%; }
body {
  font-family: Poppins, sans-serif;
  background: ${safeColor(page.backgroundColor, '#000000')};
}
.page {
  min-height: 100vh;
  background-color: ${safeColor(page.backgroundColor, '#000000')};
  background-image: ${page.backgroundImage ? 'url("fundo.jpg")' : 'none'};
  background-size: cover;
  background-position: center;
  padding: 0;
}
.inner {
  width: 100%;
  max-width: ${page.maxWidth}px;
  margin: 0 auto;
}
.perfil {
  display: block;
  width: 100%;
  height: auto;
  margin: 0;
  padding: 0;
}
.content {
  width: 100%;
  padding: 0 ${page.pagePaddingX}px ${page.pagePaddingY}px;
}
.banners {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${page.bannerGap}px;
}
.banner { appearance: none; font: inherit; }
.overlay {
  position: fixed; inset: 0; z-index: 40;
  display: none; align-items: center; justify-content: center;
  padding: 20px; background: rgba(0,0,0,.78); backdrop-filter: blur(6px);
}
.overlay.open { display: flex; }
.modal {
  position: relative; width: 100%; max-width: 380px;
  padding: 32px 24px 24px; border-radius: 20px;
  box-shadow: 0 24px 60px rgba(0,0,0,.45);
}
.modal-close {
  position: absolute; top: 10px; right: 12px; width: 36px; height: 36px;
  border: none; border-radius: 50%; background: rgba(0,0,0,.08);
  font-size: 24px; cursor: pointer;
}
.eyebrow { margin: 0; font-size: 11px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; opacity: .55; }
.brand { margin: 6px 0 0; font-size: 24px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.cupom {
  margin-top: 22px; padding: 18px 16px; border-radius: 14px;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
}
.cupom-label { font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: rgba(255,255,255,.55); }
.cupom-code { font-size: 22px; font-weight: 700; letter-spacing: .08em; word-break: break-all; }
.copy {
  margin-top: 6px; border: 1px solid rgba(201,169,110,.45); background: transparent;
  color: #fff; border-radius: 999px; padding: 8px 16px; cursor: pointer; text-transform: uppercase;
  font-size: 12px; letter-spacing: .06em;
}
.site {
  display: flex; align-items: center; justify-content: center; width: 100%;
  margin-top: 18px; padding: 15px 16px; border-radius: 12px; text-decoration: none;
  font-size: 14px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
}
`;
}

function buildScript(page: PageConfig): string {
    const coupons = page.banners
        .filter((b) => b.coupon.enabled)
        .map((b) => ({
            id: b.id,
            brand: b.coupon.brand,
            code: b.coupon.code,
            discount: b.coupon.discount,
            eyebrow: b.coupon.eyebrow,
            emptyText: b.coupon.emptyText,
            ctaLabel: b.coupon.ctaLabel,
            siteUrl: b.coupon.siteUrl,
            modalBg: safeColor(b.coupon.modalBg, '#e8ddd9'),
            modalBg2: safeColor(b.coupon.modalBg2, '#d9c8c4'),
            modalText: safeColor(b.coupon.modalText, '#1a1a1a'),
            boxBg: safeColor(b.coupon.boxBg, '#111111'),
            codeColor: safeColor(b.coupon.codeColor, '#e2c48a'),
            buttonBg: safeColor(b.coupon.buttonBg, '#111111'),
            buttonText: safeColor(b.coupon.buttonText, '#ffffff'),
        }));

    return `const CUPONS = ${JSON.stringify(coupons).replace(/</g, '\\u003c')};
const overlay = document.getElementById('overlay');
const modal = document.getElementById('modal');
function closeModal(){ overlay.classList.remove('open'); }
overlay.addEventListener('click', function(e){ if(e.target === overlay) closeModal(); });
document.addEventListener('keydown', function(e){ if(e.key === 'Escape') closeModal(); });
document.querySelectorAll('[data-coupon]').forEach(function(btn){
  btn.addEventListener('click', function(){
    const id = btn.getAttribute('data-coupon');
    const c = CUPONS.find(function(x){ return x.id === id; });
    if(!c) return;
    modal.style.background = 'linear-gradient(180deg,'+c.modalBg+' 0%,'+c.modalBg2+' 100%)';
    modal.style.color = c.modalText;
    modal.innerHTML = '';
    const close = document.createElement('button');
    close.className = 'modal-close'; close.type = 'button'; close.textContent = '×';
    close.onclick = closeModal; modal.appendChild(close);
    const eye = document.createElement('p'); eye.className = 'eyebrow'; eye.textContent = c.eyebrow || 'Cupom exclusivo'; modal.appendChild(eye);
    const brand = document.createElement('h2'); brand.className = 'brand'; brand.textContent = c.brand || ''; modal.appendChild(brand);
    const box = document.createElement('div'); box.className = 'cupom'; box.style.background = c.boxBg;
    const label = document.createElement('span'); label.className = 'cupom-label';
    const code = document.createElement('span'); code.className = 'cupom-code'; code.style.color = c.codeColor;
    if (c.code) {
      label.textContent = 'Seu cupom'; box.appendChild(label);
      code.textContent = c.code; box.appendChild(code);
      if (c.discount) { const d = document.createElement('span'); d.className = 'cupom-code'; d.style.color = c.codeColor; d.style.fontSize = '14px'; d.textContent = c.discount; box.appendChild(d); }
      const copy = document.createElement('button'); copy.type = 'button'; copy.className = 'copy'; copy.textContent = 'Copiar cupom';
      copy.onclick = function(){ navigator.clipboard.writeText(c.code).then(function(){ copy.textContent = 'Copiado!'; setTimeout(function(){ copy.textContent = 'Copiar cupom'; }, 1800); }); };
      box.appendChild(copy);
    } else if (c.discount) {
      label.textContent = 'Desconto'; box.appendChild(label);
      code.textContent = c.discount; box.appendChild(code);
    } else {
      label.textContent = 'Cupom'; box.appendChild(label);
      const empty = document.createElement('span'); empty.textContent = c.emptyText || ''; empty.style.color = 'rgba(255,255,255,.78)'; empty.style.textAlign = 'center'; box.appendChild(empty);
    }
    modal.appendChild(box);
    if (c.siteUrl) {
      const a = document.createElement('a'); a.className = 'site'; a.href = c.siteUrl; a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.style.background = c.buttonBg; a.style.color = c.buttonText; a.textContent = c.ctaLabel || 'Ir para o site';
      modal.appendChild(a);
    }
    overlay.classList.add('open');
  });
});
`;
}

function buildHtml(page: PageConfig, profileFile: string | null): string {
    const bannersHtml = page.banners.map((banner) => {
        const title = `<span style="${titleStyle(banner)}">${escapeHtml(banner.title)}</span>`;
        if (banner.coupon.enabled) {
            return `<button type="button" class="banner" data-coupon="${escapeHtml(banner.id)}" style="${bannerStyle(banner)}">${title}</button>`;
        }
        const href = banner.href || '#';
        return `<a class="banner" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="${bannerStyle(banner)}">${title}</a>`;
    }).join('\n      ');

    const profile = profileFile
        ? `<img class="perfil" src="${profileFile}" alt="Foto de perfil" />`
        : '';

    return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Meu link na bio</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Anton&family=Poppins:wght@500;600;700&display=swap" rel="stylesheet" />
  <link rel="stylesheet" href="styles.css" />
</head>
<body>
  <div class="page">
    <div class="inner">
      ${profile}
      <div class="content">
        <div class="banners">
        ${bannersHtml}
        </div>
      </div>
    </div>
  </div>
  <div id="overlay" class="overlay">
    <div id="modal" class="modal" role="dialog" aria-modal="true"></div>
  </div>
  <script src="script.js"></script>
</body>
</html>
`;
}

const README = `Seu link na bio
===============

1. Abra https://app.netlify.com/drop no computador.
2. Arraste ESTA PASTA INTEIRA (index.html, styles.css, script.js e as imagens) para a área pontilhada.
3. Espere publicar. Copie o endereço que aparecer (algo como seusite.netlify.app).
4. Cole esse endereço na bio do Instagram.

Para atualizar depois: mude os arquivos, arraste de novo no Netlify Drop (ou use o tutorial do ebook com GitHub).

Dúvidas? Chame a Links Vibe no WhatsApp e a gente monta a página para você.
`;

export async function downloadBioZip(page: PageConfig): Promise<void> {
    const zip = new JSZip();
    let profileFile: string | null = null;

    if (page.profileImage) {
        if (page.profileImage.startsWith('data:')) {
            const { blob, ext } = dataUrlToBlob(page.profileImage);
            profileFile = `perfil.${ext === 'svg' ? 'svg' : ext}`;
            zip.file(profileFile, blob);
        } else {
            const res = await fetch(page.profileImage);
            const blob = await res.blob();
            profileFile = 'perfil.jpg';
            zip.file(profileFile, blob);
        }
    }

    if (page.backgroundImage?.startsWith('data:')) {
        const { blob } = dataUrlToBlob(page.backgroundImage);
        zip.file('fundo.jpg', blob);
    } else if (page.backgroundImage) {
        const res = await fetch(page.backgroundImage);
        zip.file('fundo.jpg', await res.blob());
    }

    zip.file('index.html', buildHtml(page, profileFile));
    zip.file('styles.css', buildCss(page));
    zip.file('script.js', buildScript(page));
    zip.file('LEIA-ME.txt', README);

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'meu-link-na-bio.zip';
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}
